/**
 * F-097 — Splash 의 mini Three.js scene.
 *
 * 사용자 요청: "실제 인게임이랑 완전 똑같이". 2D 캔버스 모방으로는 게임의
 * MeshToonMaterial · ShaderMaterial atmosphere · cel outline · face sprite
 * 를 재현 불가 — 실제 게임 빌더를 그대로 호출해 별도 mini scene 에서
 * 렌더링.
 *
 * - createEarth({position}) — 게임에 쓰이는 그 동일 함수. surface 텍스쳐,
 *   toon atmosphere, scared face sprite 모두 그대로.
 * - createPlanet({tierIndex, position}) — 게임 행성 빌더. cannon body 는
 *   생성되지만 splash 에선 무시 (정적 표시만).
 * - createSceneLights — 게임과 같은 lighting (ambient + directional sun).
 *
 * 분리된 WebGL context — main 게임 캔버스와 별도. dispose 시 모든 텍스쳐/
 * geometry/material 해제 + renderer.dispose 로 GPU 리소스 정리.
 */

import {
  Group,
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
} from "three";

import { createEarth, type Earth } from "../scene/earth.ts";
import { createSceneLights } from "../scene/lighting.ts";
import { createPlanet, type Planet } from "../game/planet.ts";

interface SurroundPlanet {
  tierIndex: number;
  /** Horizontal angle (radians) around Y axis. */
  theta: number;
  /** Vertical angle (radians) — 0 = horizon, ±π/2 = pole. */
  phi: number;
  /** Distance from earth center. */
  distance: number;
}

// 지구를 둘러싸는 행성들 — 상하좌우 + 살짝 깊이 변화로 입체감. tier 0~5
// 까지 다양한 크기/색이 골고루 보이도록.
const SURROUND: SurroundPlanet[] = [
  { tierIndex: 0, theta: 0.20,        phi:  0.15, distance: 9.0 },
  { tierIndex: 1, theta: Math.PI*0.40, phi: -0.20, distance: 9.6 },
  { tierIndex: 2, theta: Math.PI*0.85, phi:  0.30, distance: 9.2 },
  { tierIndex: 3, theta: Math.PI*1.15, phi: -0.10, distance: 9.8 },
  { tierIndex: 4, theta: Math.PI*1.55, phi:  0.40, distance: 9.0 },
  { tierIndex: 5, theta: Math.PI*1.85, phi: -0.30, distance: 10.4 },
];

export interface StartSceneHandle {
  canvas: HTMLCanvasElement;
  dispose(): void;
  /** 외부에서 splash visibility 변화에 따라 일시중단 가능. */
  pause(): void;
  resume(): void;
}

export interface StartSceneOptions {
  width?: number;
  height?: number;
  /** Devicepixelratio 상한. 기본 2 — 작은 splash 에 굳이 4× 안 깔아도 충분. */
  maxDpr?: number;
}

export function createStartScene(options: StartSceneOptions = {}): StartSceneHandle {
  const width = options.width ?? 360;
  const height = options.height ?? 360;
  const maxDpr = options.maxDpr ?? 2;

  const renderer = new WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, maxDpr));
  renderer.setSize(width, height, false);
  renderer.setClearColor(0x000000, 0); // transparent — splash backdrop 위에

  const canvas = renderer.domElement;
  canvas.setAttribute("data-testid", "splash-canvas");
  canvas.style.display = "block";
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;

  const scene = new Scene();

  const lights = createSceneLights();
  scene.add(lights.ambient);
  scene.add(lights.sun);
  scene.add(lights.sun.target);

  // Camera framing: 정사각 구도로 지구가 중앙에 + 주위 행성이 frame 안에.
  const camera = new PerspectiveCamera(48, width / height, 0.1, 200);
  camera.position.set(0, 1, 22);
  camera.lookAt(0, 0, 0);

  // 회전하는 group — 행성들이 지구 주위로 천천히 떠도는 느낌.
  const orbit = new Group();
  scene.add(orbit);

  // 게임 동일 Earth — scared 얼굴 + atmosphere + 같은 surface texture.
  // 지구는 orbit 그룹 밖에 둬서 같이 안 회전하도록 (자체 spin 만).
  const earth: Earth = createEarth({ position: { x: 0, y: 0, z: 0 } });
  earth.setEmotion("scared");
  earth.setStress(0.55);
  scene.add(earth.group);

  // 게임 동일 행성들 — 지구 주위 sphere 좌표로 분산. orbit group 안에
  // 두면 group 회전이 모두에 적용 → 천천히 도는 둘러쌈 효과.
  const planets: Planet[] = [];
  for (const s of SURROUND) {
    const x = s.distance * Math.cos(s.phi) * Math.cos(s.theta);
    const z = s.distance * Math.cos(s.phi) * Math.sin(s.theta);
    const y = s.distance * Math.sin(s.phi);
    const p = createPlanet({
      tierIndex: s.tierIndex,
      position: { x, y, z },
    });
    orbit.add(p.mesh);
    planets.push(p);
  }

  let paused = false;
  let raf: number | null = null;
  let lastT = performance.now();
  let elapsed = 0;

  const tick = (now: number): void => {
    if (paused) return;
    const dt = Math.min(0.05, (now - lastT) / 1000);
    lastT = now;
    elapsed += dt;

    earth.tick(dt);

    // 행성 group 이 지구 주위로 천천히 회전 — 둘러싸인 분위기.
    orbit.rotation.y += dt * 0.12;
    // 약간의 좌우 tilt — 단조롭지 않게.
    orbit.rotation.z = Math.sin(elapsed * 0.4) * 0.05;

    // 각 행성이 자체 spin (idle motion).
    for (let i = 0; i < planets.length; i += 1) {
      planets[i]!.mesh.rotation.y += dt * 0.4 * (i % 2 === 0 ? 1 : -1);
    }

    renderer.render(scene, camera);
    raf = requestAnimationFrame(tick);
  };

  const start = (): void => {
    if (raf !== null) return;
    paused = false;
    lastT = performance.now();
    raf = requestAnimationFrame(tick);
  };

  const pause = (): void => {
    paused = true;
    if (raf !== null) {
      cancelAnimationFrame(raf);
      raf = null;
    }
  };

  const dispose = (): void => {
    pause();
    earth.detach();
    for (const p of planets) {
      p.destroy();
    }
    renderer.dispose();
    renderer.forceContextLoss?.();
  };

  start();

  return { canvas, dispose, pause, resume: start };
}
